"use client";

import { useCallback, useRef, useState, type PointerEvent } from "react";
import {
  m,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "motion/react";
import { BeerCard } from "./beer-card";
import { BeerCardBack } from "./beer-card-back";
import {
  MOUSE_PUSH_MAX,
  cardShadow,
  flipTransition,
  mousePushSpring,
  randomSettleTilt,
} from "./beer-card.animation";
import type { BeerCardProps } from "./beer-card.interface";
import messages from "@/messages/en.json";

const t = messages.whatsOnTap.beerCard.interaction;

/**
 * Ease the linear cursor offset (-1..1) toward the card edge so movement
 * is subtle in the centre and only really kicks in near the corners.
 */
function easeCursor(offset: number): number {
  const clamped = Math.max(-1, Math.min(1, offset));
  return Math.sign(clamped) * Math.pow(Math.abs(clamped), 1.6);
}

/**
 * Interactive shell around `<BeerCard>`.
 *
 *  1. Mouse push-away — soft cursor-eased translate via spring (only
 *     when the card is face-up so a flipped card isn't twitching).
 *  2. Click flip — playing-card-style 3D rotation around Y, landing
 *     with a fresh random settle-tilt on Z (~2–5°, re-rolled each flip)
 *     so flipped cards look casually scattered rather than identically
 *     aligned.
 *
 * Reduced motion → toggles front/back via display, no transforms.
 */
export function BeerCardInteractive({ beer, compact = false }: BeerCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  // Re-rolled each time the card flips to the back so repeat-flipping
  // doesn't land at the same angle.
  const [settleTilt, setSettleTilt] = useState(0);
  const reduced = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const x = useSpring(rawX, mousePushSpring);
  const y = useSpring(rawY, mousePushSpring);
  const rotateY = useTransform(x, (v) => v * -0.3);
  const rotateXTilt = useTransform(y, (v) => v * 0.3);

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (reduced || isFlipped) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const nx = (event.clientX - rect.left) / rect.width - 0.5;
      const ny = (event.clientY - rect.top) / rect.height - 0.5;
      rawX.set(easeCursor(-nx * 2) * MOUSE_PUSH_MAX);
      rawY.set(easeCursor(-ny * 2) * MOUSE_PUSH_MAX);
    },
    [reduced, isFlipped, rawX, rawY],
  );

  const handlePointerLeave = useCallback(() => {
    rawX.set(0);
    rawY.set(0);
  }, [rawX, rawY]);

  const toggle = useCallback(() => {
    setIsFlipped((prev) => {
      const next = !prev;
      // Fresh tilt on every flip-to-back; un-flip animates to 0 so the
      // current tilt value doesn't need touching.
      if (next) setSettleTilt(randomSettleTilt());
      return next;
    });
  }, []);

  const label = (isFlipped ? t.hide : t.reveal).replace("{name}", beer.name);

  // Reduced motion: render front XOR back, no transforms.
  if (reduced) {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-pressed={isFlipped}
        aria-label={label}
        className="focus-visible:outline-copper-kettle block w-full cursor-pointer text-left select-none focus-visible:outline-2 focus-visible:outline-offset-4"
      >
        {isFlipped ? (
          <BeerCardBack beer={beer} />
        ) : (
          <BeerCard beer={beer} compact={compact} />
        )}
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative select-none"
      style={{ perspective: 1400 }}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      {/* Mouse push-away layer — translates the card under the cursor. */}
      <m.div
        className="relative"
        style={{
          x,
          y,
          rotateY,
          rotateX: rotateXTilt,
          transformStyle: "preserve-3d",
        }}
      >
        {/* Flip layer — rotates Y on click, plus a small settle-tilt on
            Z. Box-shadow lives here too so the shadow follows the card
            as it tilts (instead of sitting as a ghost rectangle behind
            the card's original position). `rounded-card` keeps the
            shadow on the card's curved corners.

            `pointer-events-none` so the card faces never compete with
            the button overlay for hit-testing — in a preserve-3d
            context the back face and the button sit at the same Z
            depth, which causes the cursor to flicker between pointer
            and text/default. The button below is the only intended
            click target. */}
        <m.div
          className="rounded-card pointer-events-none relative"
          style={{ transformStyle: "preserve-3d" }}
          animate={{
            rotateY: isFlipped ? 180 : 0,
            rotate: isFlipped ? settleTilt : 0,
            boxShadow: isFlipped ? cardShadow.settled : cardShadow.rest,
          }}
          initial={{ boxShadow: cardShadow.rest }}
          transition={flipTransition}
        >
          {/* Front face — provides the natural card height. */}
          <div style={{ backfaceVisibility: "hidden" }}>
            <BeerCard beer={beer} compact={compact} />
          </div>

          {/* Back face — same bounds as the front, pre-rotated 180° so
              its surface faces away by default; when the parent rotates
              180° it ends up facing the viewer. */}
          <div
            className="absolute inset-0"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <BeerCardBack beer={beer} />
          </div>
        </m.div>

        {/* Toggle button — invisible overlay covering the whole card so
            tapping anywhere flips it. Sits above both faces in z-order
            but outside the 3D-flipped subtree so it isn't rotated. */}
        <button
          type="button"
          onClick={toggle}
          aria-pressed={isFlipped}
          aria-label={label}
          className="focus-visible:outline-copper-kettle absolute inset-0 z-10 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4"
        />
      </m.div>
    </div>
  );
}
